import { RtmMessage } from 'agora-rtm-sdk';
import React, {
  ReactElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Draggable, {
  ControlPosition,
  DraggableBounds,
  DraggableData,
  DraggableEvent,
} from 'react-draggable';
import { AiOutlineClose } from 'react-icons/ai';
import { Debug } from '../../../../common/utils/debugLogs';
import {
  useDeleteBoardMutation,
  useUpdateBoardMutation,
} from '../../../../services/api/apiProvider';
import { ResponseBoard } from '../../../../services/api/models/response/responseBoard';
import { ResponsePosition } from '../../../../services/api/models/response/responsePosition';
import { LiveProps } from '../../LiveContainer';
import { MessageType } from '../../types/messageType';

import './LiveBoard.scss';
import MobileDetect from 'mobile-detect';

export const LiveBoard = (props: LiveProps): ReactElement => {
  const board = props.liveState.liveData?.board;
  const [showSave, setShowSave] = useState(false);
  const [text, setText] = useState(board?.message);

  const boardRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<ControlPosition>();
  const [bounces, setBounces] = useState<DraggableBounds>();

  const [updateBoard] = useUpdateBoardMutation();
  const [deleteBoard] = useDeleteBoardMutation();

  // initialize
  useEffect(() => {
    listenChannelMessage();
  }, []);

  // update board position when board data was changed
  useLayoutEffect(() => {
    if (board) {
      calculatePosition(board.point);
    }

    // listener when '.live-media-player__content' element changes size.
    const resizeObserver = new ResizeObserver(() => {
      if (board) {
        calculatePosition(board.point);
      }
    });

    const videoPlayerElement = document.querySelector(
      '.live-media-player__content',
    );
    if (videoPlayerElement) {
      resizeObserver.observe(videoPlayerElement);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [board]);

  /**
   * Calculate board position to display
   */
  const calculatePosition = (point: ResponsePosition) => {
    const boardElement = boardRef.current;
    const parentElement = boardRef.current?.parentElement;
    if (!boardElement || !parentElement) {
      return undefined;
    }

    // bounces value
    const xMin = boardElement.clientWidth / 2;
    const xMax = parentElement.clientWidth - boardElement.clientWidth / 2;
    const yMin = boardElement.clientHeight / 2;
    const yMax =
      parentElement.clientHeight - 140 - boardElement.clientHeight / 2;

    // calculate board position with point (rate with the parent element).
    // origin point is center.
    let x = (point.x * parentElement.clientWidth) / 100;
    let y = (point.y * parentElement.clientHeight) / 100;

    // keep board position inside bouces
    if (x < xMin) {
      x = xMin;
    }
    if (x > xMax) {
      x = xMax;
    }
    if (y < yMin) {
      y = yMin;
    }
    if (y > yMax) {
      y = yMax;
    }

    // set board postion
    setPosition({ x, y });

    // set bounces for draggable element
    setBounces({
      left: xMin,
      top: yMin,
      right: xMax,
      bottom: yMax,
    });
  };

  /**
   * For audience, listen RTM message about board.
   * Handle messages: NOTIFY_BOARD.
   */
  const listenChannelMessage = () => {
    props.liveState.rtmEngine?.on(
      'ChannelMessage',
      (message: RtmMessage): void => {
        if (message.messageType === 'TEXT') {
          const content = JSON.parse(message.text);

          // UPDATE BOARD
          if (content.type === MessageType.NOTIFY_BOARD) {
            const newBoard = content.value as ResponseBoard;
            if (!newBoard) {
              Debug.log('DELETE BOARD');
              props.deleteLiveBoard();
              return;
            }

            Debug.log('UPDATE BOARD ' + JSON.stringify(newBoard));
            props.updateLiveBoard(newBoard);
            setText(newBoard.message);
          }
        }
      },
    );
  };

  /**
   * Update message
   */
  const handleUpdateMessage = () => {
    setShowSave(false);

    const messageElement = document.querySelector('.live-board__input');
    const message = messageElement?.textContent;

    if (!board || !message) {
      return;
    }

    props.updateLiveBoard({ ...board, message });

    updateBoard({
      id: board.id,
      message,
    });
  };

  /**
   * Delete board
   */
  const handleDeleteBoard = () => {
    if (!board) {
      return;
    }

    // reset state
    setText('');
    setShowSave(false);

    props.deleteLiveBoard();
    deleteBoard(board.id);
  };

  /**
   * Update board position when stop dragging.
   * @param e Event
   * @param data Draggable element data
   */
  const handleDragStop = (e: DraggableEvent, data: DraggableData) => {
    const boardElement = boardRef.current;
    const parentElement = boardRef.current?.parentElement;

    if (!board || !boardElement || !parentElement) {
      return;
    }

    const pointX = Math.round((data.x * 100) / parentElement.clientWidth);
    const pointY = Math.round((data.y * 100) / parentElement.clientHeight);

    // check same position
    if (board.point.x === pointX && board.point.y === pointY) {
      return;
    }

    props.updateLiveBoard({
      ...board,
      point: {
        x: pointX,
        y: pointY,
      },
    });

    updateBoard({
      id: board.id,
      point: {
        x: pointX,
        y: pointY,
      },
    });
  };

  if (!board) {
    return <></>;
  }

  return (
    <Draggable
      nodeRef={boardRef}
      axis="both"
      handle=".live-board"
      positionOffset={{ x: '-50%', y: '-50%' }}
      position={position}
      bounds={bounces}
      disabled={props.liveState.role === 'audience'}
      onStop={handleDragStop}
    >
      <div
        ref={boardRef}
        className="live-board"
        style={{ backgroundImage: `url(${board.type.image.uri})` }}
      >
        {props.liveState.role === 'host' && (
          <div
            className="live-board__remove-btn"
            onClick={handleDeleteBoard}
            onTouchStart={handleDeleteBoard}
          >
            <AiOutlineClose />
          </div>
        )}

        <div
          className="live-board__input"
          style={{ color: board.type.textColor }}
          contentEditable={props.liveState.role === 'host'}
          suppressContentEditableWarning={true}
          onInput={() => {
            setShowSave(true);
          }}
          onTouchStart={(event) => {
            $(event.target).focus();
          }}
        >
          {text}
        </div>

        {showSave && props.liveState.role === 'host' && (
          <div
            className="live-board__save-btn"
            onClick={handleUpdateMessage}
            onTouchStart={handleUpdateMessage}
          >
            Save
          </div>
        )}
      </div>
    </Draggable>
  );
};
